import {DependencyInjectId} from '@@/package/const/DependencyInjectId';
import HttpClient from '@@/package/network/HttpClient';
import {type AxiosResponse} from 'axios';
import {type ApplicationContextInterface} from '@@/package/application/applicationContext';
import type LearningCourseResponse from "~~/package/apis/learning/response/LearningCourseResponse";
import type TaskResponse from "~~/package/apis/learning/response/TaskResponse";
import {LangType} from "~~/package/mapper/learning/model/LangType";
import type TaskCompleteRequest from "~~/package/apis/learning/request/TaskCompleteRequest";
import type TaskAnswerSaveRequest from "~~/package/apis/learning/request/TaskAnswerSaveRequest";
import type TaskCompleteResponse from "~~/package/apis/learning/response/TaskCompleteResponse";
import type TaskAnswerResponse from "~~/package/apis/learning/response/TaskAnswerResponse";
import type CustomResponse from "~~/package/types/ResponseType";
import type {LearningTokenData} from "~~/package/mapper/learning/model/LearningTokenData";
import {base64ToUtf8, utf8ToBase64} from "~~/package/utils/Base64Util";

export default class LearningApi {

    protected http: HttpClient;

    constructor(context: ApplicationContextInterface) {
        this.http = context.getService<HttpClient>(DependencyInjectId.HttpClient);
    }

    public getLearningCourse(courseId: number, lang: string): Promise<AxiosResponse<CustomResponse<LearningCourseResponse>>> {
        return this.http.get<AxiosResponse<CustomResponse<LearningCourseResponse>>>(`/course/${courseId}/learning`, {}, true, lang);
    }

    public getTask(taskId: number, lang: string): Promise<AxiosResponse<CustomResponse<TaskResponse>>> {
        return this.http.post<AxiosResponse<CustomResponse<TaskResponse>>, { [key: string]: number }>("/task/learning", {taskId}, true, lang);
    }

    public getToken(courseId: number, taskId: number, langType: LangType, locale: string, domain?: string, domainId?: number): Promise<AxiosResponse<CustomResponse<string>>> {
        // domain/domainId are only sent for per-assessment isolated labs (e.g. proSkillAssure).
        // Omitting them for TRAINING/learning labs keeps the exact current request shape.
        const params: { [key: string]: number | string | LangType } = {courseId, taskId, langType, locale};
        if (domain !== undefined) params.domain = domain;
        if (domainId !== undefined) params.domainId = domainId;
        return this.http.get<AxiosResponse<CustomResponse<string>>>("/v2/tasks/learning/ssToken", params, true);
    }

    public requestToSubChapterComplete(request: TaskCompleteRequest, tokenData: LearningTokenData): Promise<AxiosResponse<CustomResponse<TaskCompleteResponse>>> {
        return this.http.post<AxiosResponse<CustomResponse<TaskCompleteResponse>>, TaskCompleteRequest>(
            "/v2.2/tasks/learning/complete", request,
            true,
            null,
            false,
            {hmac: tokenData.hmac, issued_at: tokenData.issuedAt});
    }

    public getTaskAnswer(taskId: number): Promise<AxiosResponse<CustomResponse<TaskAnswerResponse>>> {
        return this.http.get<AxiosResponse<CustomResponse<TaskAnswerResponse>>>(`/task/${taskId}/learning/answerCode`, {}, true);
    }

    public saveCodeHistory(taskId: number, code: string): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.post<AxiosResponse<CustomResponse<boolean>>, {[key:string]: number|string}>("/v2.2/tasks/learning/codeHistory", {taskId, code}, true);
    }

    public saveCode(request: TaskAnswerSaveRequest): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.post<AxiosResponse<CustomResponse<boolean>>, TaskAnswerSaveRequest>("/v2.2/tasks/learning/answerCode", request, true);
    }

    public saveUserCode(taskId: number, domainType: string, domainId: number, code: string): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.post<AxiosResponse<CustomResponse<boolean>>, {[key:string]: number|string}>(`/v2.2/tasks/${taskId}/code?domainType=${domainType}&domainId=${domainId}`, {code}, true);
    }

    public getLatestSavedCode(taskId: number, domainType: string, domainId: number): Promise<AxiosResponse<CustomResponse<string>>> {
        return this.http.get<AxiosResponse<CustomResponse<string>>>(`/tasks/${taskId}/code?domainType=${domainType}&domainId=${domainId}`, {}, true);
    }

    public getTaskForValidate(uuid: string): Promise<AxiosResponse<CustomResponse<TaskResponse>>> {
        return this.http.get<AxiosResponse<CustomResponse<TaskResponse>>>(`/tasks/${uuid}`, {}, true);
    }

    public getTaskValidity(uuid: string, request: TaskCompleteRequest, tokenData: LearningTokenData): Promise<AxiosResponse<CustomResponse<TaskCompleteResponse>>> {
        return this.http.post<AxiosResponse<CustomResponse<TaskCompleteResponse>>, TaskCompleteRequest>(
            `/tasks/${uuid}/validate`,
            request,
            true,
            null,
            false,
            {hmac: tokenData.hmac, issued_at: tokenData.issuedAt}
        );
    }

    public saveTaskDuration(taskId: number, domainType: string, domainId: number, amountInSec: number): Promise<AxiosResponse<CustomResponse<boolean>>> {
        return this.http.put<AxiosResponse<CustomResponse<boolean>>, { [key: string]: number | string }>(
            `/v2.2/tasks/${taskId}/stats/duration?domainType=${domainType}&domainId=${domainId}&amountInSec=${amountInSec}`,
            {},
            true
        );
    }
}
